import { useT } from "@/i18n";
import { useMemo } from "react";
import {
  AnswerLine,
  FeedbackBox,
  Hero,
  PromptImage,
  ReadOnlyCard,
  Section,
  SpeakerButton,
} from "./ReadOnlyKit";
import { rs } from "./readOnlyStyles";
import { getScoreColor, getWordBoundaryIndices } from "./scoring";
import type { ReadOnlyTemplateProps } from "./types";
import { useReviewAudio } from "./useReviewAudio";

interface LabeledScore {
  label: string;
  score: number;
}

/**
 * The word coloured by the finest scores this play holds: per syllable when the slide authored
 * syllable audio, otherwise per word, otherwise the whole word by the play's own score — a play is
 * never left uncoloured just because nobody split the word up.
 */
function ScoredWord({
  word,
  syllables,
  wordScores,
  score,
}: {
  word: string;
  syllables: LabeledScore[];
  wordScores: LabeledScore[];
  score: number | null;
}) {
  const wordBoundaryIndices = useMemo(
    () =>
      getWordBoundaryIndices(
        word,
        syllables.map((syllable) => syllable.label)
      ),
    [word, syllables]
  );

  const parts = syllables.length > 0 ? syllables : wordScores;
  if (parts.length === 0) {
    return (
      <p style={{ ...rs.heroWord, ...(score !== null && { color: getScoreColor(score) }) }}>
        {word || "—"}
      </p>
    );
  }

  // Syllables carry no spaces of their own, so word breaks come from getWordBoundaryIndices; word
  // scores are already one part per word.
  const isSplitBySyllable = syllables.length > 0;

  return (
    <p style={rs.heroWord}>
      {parts.map((part, index) => (
        <span key={`${part.label}-${index}`} style={{ color: getScoreColor(part.score) }}>
          {(isSplitBySyllable ? wordBoundaryIndices.has(index) : index > 0) ? " " : ""}
          {part.label}
        </span>
      ))}
    </p>
  );
}

/** A past pronunciation play: the word as it was set, coloured by how it scored. */
export function VocabularyReadOnly({
  snapshot,
  response,
  score,
  recordingUrl,
}: ReadOnlyTemplateProps<"vocabulary">) {
  const t = useT();
  const { word, sentence, image } = snapshot.slideData;
  const { playingUrl, toggle } = useReviewAudio();

  const myRecording = recordingUrl(response.take?.recordingId);

  return (
    <ReadOnlyCard title={snapshot.name} score={score}>
      <Hero>
        <PromptImage media={image} label={t["readonly.wordImage"]} />
        <ScoredWord
          word={word ?? ""}
          syllables={response.syllableScores}
          wordScores={response.wordScores ?? []}
          score={score}
        />
        <SpeakerButton
          label={t["readonly.myRecording"]}
          url={myRecording}
          isPlaying={playingUrl === myRecording}
          onPress={() => toggle(myRecording)}
          variant="student"
        />
        {sentence ? <p style={rs.heroSubtext}>{sentence}</p> : null}
      </Hero>

      {response.wordScores && response.wordScores.length > 0 && (
        <Section label={t["readonly.perWord"]}>
          {response.wordScores.map((wordScore, index) => (
            <AnswerLine
              key={`${wordScore.label}-${index}`}
              studentAnswer={wordScore.label}
              score={wordScore.score}
            />
          ))}
        </Section>
      )}

      {response.explanation ? <FeedbackBox text={response.explanation} /> : null}
    </ReadOnlyCard>
  );
}
